import React from 'react';
import { Box, Button } from '@cad-arc';
import { CadNextNotification, ColumnDefinitionEnumType } from '@mark43/cad-api';
import { observer } from 'mobx-react-lite';
import { colors } from '../../colors/colors';
import { useBroadcast } from '../../common/broadcast-channel/use-broadcast';
import { Tooltip } from '../../common/components/tooltip';
import { EventNotificationsCounter } from '../../event-panel/tabs/components/event-notifications-counter';
import { getCounterNotifications } from '../../event-panel/tabs/components/get-counter-notifications';
import { useEventNotificationDisplayNames } from '../../event-notifications/use-event-notification-display-names';
import { useEventNotificationsStore } from '../../event-notifications/use-event-notifications';
import { UnitQueueRowData } from '../../socket/stream-types';
import { columnDefinitionNamesByEventNotificationName } from '../column-definition.enum';
import { useQueueStore } from '../context/use-queue-store';
import { RawQueueRowData } from '../store/types-2';
import { getEventQueueRowData } from '../utils/get-event-queue-row-data';

// The gutter's sizes come from the queue density variables in index.css

/** Width of the fixed column that precedes a queue's configurable columns */
export const QUEUE_GUTTER_WIDTH = 'var(--queue-gutter-width)';

export const queueRowGutterTestIds = {
    CLEAR_UPDATES_BUTTON: 'queue-row-clear-updates',
} as const;

const PIN_TO_PRIMARY_BORDER = `var(--queue-pin-border-width) solid ${colors.backupUnitBorder}`;

const COUNTER_STYLE: React.CSSProperties = {
    height: 'var(--queue-counter-size)',
    minWidth: 'var(--queue-counter-size)',
    lineHeight: 'var(--queue-counter-size)',
    fontSize: 'var(--queue-counter-font-size)',
};

/**
 * Fixed first cell of every queue row. It has no header and can't be moved or resized. It holds the
 * row indicators so they don't have to share whichever configurable column happens to be first:
 * - the event's update badge, when the queue shows updates. Clicking it clears all of the event's
 *   updates, in old CAD too.
 * - the bracket linking backup units to their primary, when backups are pinned to primary units
 */
export const QueueRowGutter = observer(function QueueRowGutter({
    data,
    notifications,
}: {
    data: RawQueueRowData;
    notifications: CadNextNotification[];
}): React.ReactElement {
    const queueStore = useQueueStore();
    const eventNotificationsStore = useEventNotificationsStore();
    const eventNotificationDisplayNames = useEventNotificationDisplayNames();
    const { showUpdates, pinBackupsToPrimaryUnits } = queueStore.workspacePanel.queue;
    const unitData = data as UnitQueueRowData;
    const isPinnedBackup = pinBackupsToPrimaryUnits && !!unitData.backingUpUnitQueueView;
    const hasPinnedBackups = pinBackupsToPrimaryUnits && !!unitData.hasBackups;
    const eventId = getEventQueueRowData(data)?.id;
    const { postMessage } = useBroadcast('dismiss-event-notifications');

    const clearUpdates = React.useCallback(
        function clearEventUpdates() {
            if (eventId === undefined) {
                return;
            }
            // Old CAD dismisses its event panel updates one column at a time, so name every column
            // these updates show in, as if each highlighted cell had been clicked
            const columnDefinitionNames = new Set(
                notifications.flatMap(
                    (notification) =>
                        Object.keys(
                            columnDefinitionNamesByEventNotificationName[
                                notification.notificationType
                            ] ?? {}
                        ) as ColumnDefinitionEnumType[]
                )
            );
            columnDefinitionNames.forEach((columnDefinitionName) =>
                postMessage({
                    type: 'DISMISS_LEGACY_CAD_EVENT_PANEL_NOTIFICATIONS',
                    payload: { eventId, columnDefinitionName },
                })
            );
            eventNotificationsStore.removeAllNotificationsForEvent(eventId);
        },
        [eventId, eventNotificationsStore, notifications, postMessage]
    );

    return (
        <Box
            className="queue-row-gutter"
            borderRight={isPinnedBackup ? PIN_TO_PRIMARY_BORDER : undefined}
            borderBottom={hasPinnedBackups ? PIN_TO_PRIMARY_BORDER : undefined}
            data-testid={hasPinnedBackups ? 'pin-to-primary-bottom-border' : undefined}
        >
            {showUpdates && notifications.length > 0 ? (
                <Tooltip
                    overlay={getCounterNotifications(notifications)
                        .map((notification) =>
                            notification.notificationPriority === 'HIGH'
                                ? `High Priority ${
                                      eventNotificationDisplayNames[notification.notificationType]
                                  }`
                                : eventNotificationDisplayNames[notification.notificationType]
                        )
                        .join(', ')}
                >
                    {/* A button, so clearing the updates doesn't also select or deselect the row */}
                    <Box as="span" className="queue-cell-button">
                        <Button
                            variant="ghost"
                            onClick={clearUpdates}
                            aria-label="Clear updates"
                            data-testid={queueRowGutterTestIds.CLEAR_UPDATES_BUTTON}
                        >
                            <EventNotificationsCounter
                                notifications={notifications}
                                style={COUNTER_STYLE}
                            />
                        </Button>
                    </Box>
                </Tooltip>
            ) : null}
        </Box>
    );
});
